if (!customElements.get('recipient-form')) {
	customElements.define('recipient-form', class RecipientForm extends HTMLElement {
		constructor() {
			super();
			this.checkboxInput = this.querySelector(`#Recipient-checkbox-${ this.dataset.sectionId }`);
			this.checkboxInput.disabled = false;
			this.hiddenControlField = this.querySelector(`#Recipient-control-${ this.dataset.sectionId }`);
			this.hiddenControlField.disabled = true;
			this.emailInput = this.querySelector(`#Recipient-email-${ this.dataset.sectionId }`);
			this.nameInput = this.querySelector(`#Recipient-name-${ this.dataset.sectionId }`);
			this.messageInput = this.querySelector(`#Recipient-message-${ this.dataset.sectionId }`);
			this.sendonInput = this.querySelector(`#Recipient-send-on-${ this.dataset.sectionId }`);
			this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${ this.dataset.sectionId }`);
			if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset();

			this.currentProductVariantId = this.dataset.productVariantId;
			this.addEventListener('change', this.onChange.bind(this));
			this.onChange();

			this.emailInput.addEventListener('input', this._onEmailInput.bind(this));
			this.emailInput.addEventListener('invalid', this._onEmailInvalid.bind(this));

			if (this.sendonInput) {
				const toLocalDateString = (d) => {
					const yyyy = d.getFullYear();
					const mm = String(d.getMonth() + 1).padStart(2, '0');
					const dd = String(d.getDate()).padStart(2, '0');
					return `${yyyy}-${mm}-${dd}`;
				};
				const maxDate = new Date();
				maxDate.setDate(maxDate.getDate() + 90);
				this.sendonInput.max = toLocalDateString(maxDate);
				this.sendonInput.min = toLocalDateString(new Date());

				this.sendonInput.addEventListener('change', () => {
					this.sendonInput.classList.toggle('has-value', !!this.sendonInput.value);
				});
			}
		}

		cartUpdateUnsubscriber = undefined;
		variantChangeUnsubscriber = undefined;
		cartErrorUnsubscriber = undefined;

		connectedCallback() {
			this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
				if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
					this.resetRecipientForm();
				}
			});

			this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
				if (event.data.sectionId === this.dataset.sectionId) {
					this.currentProductVariantId = event.data.variant.id.toString();
				}
			});

			this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
				if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
					this.displayErrorMessage(event.message, event.errors);
				}
			});

			// Client-side validation: intercept form submit before it reaches the server
			this._boundValidate = this.validateBeforeSubmit.bind(this);
			const form = this.closest('form');
			if (form) form.addEventListener('submit', this._boundValidate, true);
		}

		disconnectedCallback() {
			const form = this.closest('form');
			if (form && this._boundValidate) form.removeEventListener('submit', this._boundValidate, true);

			if (this.cartUpdateUnsubscriber) {
				this.cartUpdateUnsubscriber();
			}

			if (this.variantChangeUnsubscriber) {
				this.variantChangeUnsubscriber();
			}

			if (this.cartErrorUnsubscriber) {
				this.cartErrorUnsubscriber();
			}
		}

		onChange() {
			if (this.checkboxInput.checked) {
				this.enableInputFields();
			} else {
				this.clearInputFields();
				this.disableInputFields();
				this.clearErrorMessage();
			}
		}

		inputFields() {
			return [
				this.emailInput,
				this.nameInput,
				this.messageInput,
				this.sendonInput
			];
		}

		disableableFields() {
			return [...this.inputFields(), this.offsetProperty];
		}

		clearInputFields() {
			this.inputFields().forEach((field) => field.value = '');
			if (this.sendonInput) this.sendonInput.classList.remove('has-value');
		}

		enableInputFields() {
			this.disableableFields().forEach((field) => field.disabled = false);
		}

		disableInputFields() {
			this.disableableFields().forEach((field) => field.disabled = true);
		}

		_onEmailInput() {
			const errorEl = this.querySelector(`#RecipientForm-email-error-${this.dataset.sectionId}`);
			if (errorEl && !errorEl.classList.contains('hidden')) {
				errorEl.classList.add('hidden');
				errorEl.querySelector('.error-message').innerText = '';
				this.emailInput.setAttribute('aria-invalid', 'false');
			}
		}

		_onEmailInvalid(evt) {
			evt.preventDefault();
			const errorEl = this.querySelector(`#RecipientForm-email-error-${this.dataset.sectionId}`);
			if (errorEl) {
				errorEl.querySelector('.error-message').innerText = window.recipientStrings?.email_invalid || 'Email is invalid';
				errorEl.classList.remove('hidden');
			}
			this.emailInput.setAttribute('aria-invalid', 'true');
		}

		validateBeforeSubmit(evt) {
			if (!this.checkboxInput.checked) return;

			if (!this.emailInput.value.trim()) {
				evt.preventDefault();
				evt.stopImmediatePropagation();
				const errorEl = this.querySelector(`#RecipientForm-email-error-${this.dataset.sectionId}`);
				if (errorEl) {
					errorEl.querySelector('.error-message').innerText = window.recipientStrings?.email_label || 'Email is required';
					errorEl.classList.remove('hidden');
				}
				this.emailInput.setAttribute('aria-invalid', 'true');
				this.emailInput.focus();
				return;
			}

			if (this.sendonInput?.value) {
				const selectedDate = new Date(this.sendonInput.value + 'T00:00:00');

				const minDate = new Date();
				minDate.setHours(0, 0, 0, 0);

				const maxDate = new Date();
				maxDate.setDate(maxDate.getDate() + 90);

				const sendOnErrorEl = this.querySelector(`#RecipientForm-send_on-error-${this.dataset.sectionId}`);

				if (selectedDate < minDate) {
					evt.preventDefault();
					evt.stopImmediatePropagation();
					if (sendOnErrorEl) {
						sendOnErrorEl.querySelector('.error-message').innerText = window.recipientStrings?.send_on_error || 'Send on date must be in the future';
						sendOnErrorEl.classList.remove('hidden');
					}
					this.sendonInput.setAttribute('aria-invalid', 'true');
					this.sendonInput.focus();
					return;
				}

				if (selectedDate > maxDate) {
					evt.preventDefault();
					evt.stopImmediatePropagation();
					if (sendOnErrorEl) {
						sendOnErrorEl.querySelector('.error-message').innerText = window.recipientStrings?.send_on_max_error || 'Send on must be within 90 days from now';
						sendOnErrorEl.classList.remove('hidden');
					}
					this.sendonInput.setAttribute('aria-invalid', 'true');
					this.sendonInput.focus();
					return;
				}
			}
		}

		displayErrorMessage(title, body) {
			this.clearErrorMessage();
			if (typeof body === 'object') {
				return Object.entries(body).forEach(([key, value]) => {
					const errorMessageId = `RecipientForm-${ key }-error-${ this.dataset.sectionId }`
					const fieldSelector = `#Recipient-${ key }-${ this.dataset.sectionId }`;
					const message = `${value.join(', ')}`;
					const errorMessageElement = this.querySelector(`#${errorMessageId}`);
					const errorTextElement = errorMessageElement?.querySelector('.error-message')
					if (!errorTextElement) return;

					errorTextElement.innerText = `${message}.`;
					errorMessageElement.classList.remove('hidden');

					const inputElement = this[`${key}Input`];
					if (!inputElement) return;

					inputElement.setAttribute('aria-invalid', true);
					inputElement.setAttribute('aria-describedby', errorMessageId);
				});
			} else if (body || title) {
				const errorEl = this.querySelector(`#RecipientForm-email-error-${ this.dataset.sectionId }`);
				if (errorEl) {
					errorEl.querySelector('.error-message').innerText = body || title;
					errorEl.classList.remove('hidden');
				}
			}
		}

		createErrorListItem(target, message) {
			const li = document.createElement('li');
			const a = document.createElement('a');
			a.setAttribute('href', target);
			a.innerText = message;
			li.appendChild(a);
			li.className = "error-message";
			return li;
		}

		clearErrorMessage() {
			this.querySelectorAll('.recipient-fields .form__message').forEach(field => {
				field.classList.add('hidden');
				const textField = field.querySelector('.error-message');
				if (textField) textField.innerText = '';
			});

			[this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach(inputElement => {
				inputElement.setAttribute('aria-invalid', false);
				inputElement.removeAttribute('aria-describedby');
			});
		}

		resetRecipientForm() {
			if (this.checkboxInput.checked) {
				this.checkboxInput.checked = false;
				this.clearInputFields();
				this.clearErrorMessage();
			}
		}
	});
}